import { useEffect, useRef, useState } from "react";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import useContactBook from "hooks/contacts/useContactBook";
import { useLiveContact } from "hooks/useLiveContact";
import { postProcessContact } from "utils/contacts/helper";
import type { ContactReadScope } from "hooks/contacts/contactDocumentRules";
import type { RealEstateContact } from "../../../../@types/sharedSchema";

/**
 * Hydrates ONE contact for the CRM side panel.
 *
 * The list row is a projection (`CrmContactRow`: 27 flat fields out of Postgres). The
 * panel body — `ViewContactDialog` → `ContactTableNestedRowTabs` — wants a whole
 * `RealEstateContact`. So exactly one read per opened contact, and WHICH read is an
 * access-control decision, not a performance one:
 *
 * ┌ the viewer OWNS the document ──────────────────────────────────────────────────┐
 * │ `useLiveContact` → onSnapshot on dashboard-users-v2/{viewer}/contacts/{id}.     │
 * │ Safe by construction: the path is keyed on the VIEWER's own email, so it can    │
 * │ only ever return a document the viewer owns. Live, so an edit made on the full  │
 * │ page or from the mobile app shows up in the panel without a refetch. This is    │
 * │ the pattern `ContactDetailView` already uses.                                   │
 * └────────────────────────────────────────────────────────────────────────────────┘
 * ┌ anybody else's document (the org view) ────────────────────────────────────────┐
 * │ POST /api/v1/contacts/get-details, `dataSources: ['contacts']`.                 │
 * │ NEVER a Firestore listener. A listener would return the raw document — no       │
 * │ redaction, no `seeNotes` gate, no audit record — and would need a rule allowing │
 * │ cross-user contact reads, which would then apply to every other read path too.  │
 * │ The endpoint resolves org membership server-side from `dashboard-orgs`          │
 * │ (`filterByUserAccess` → `resolveUserOrgIds`), NOT from the client-writable      │
 * │ `user.organizationIds`, and runs the free-text blur for non-admins. Nothing     │
 * │ about who may see what comes from this request.                                 │
 * └────────────────────────────────────────────────────────────────────────────────┘
 *
 * ── The gap this used to carry is CLOSED, and the correction matters ────────────
 *
 * This block used to warn that `get-details` scoped by the contact document's own
 * `organizationIds` — a write-time snapshot that is never backfilled, missing on 57.6% of
 * the pilot org's contacts — so the agency list could legitimately return a row the panel
 * then declined to hydrate. That would have made more than half of every agency row open
 * a dead panel, which is not a gap the agency view could have shipped with.
 *
 * `contacts.service.filterByOwnerAccess` now scopes by the record's OWNER against
 * `resolveContactAccess`'s per-request `poolByOwner` map, which is the same question the
 * Postgres list answers and resolved from the same source (`dashboard-orgs`). The two
 * agree by construction. It also honours `pool: 'none'` — a member whose agency turned
 * the pool off gets nothing here, which is the correct pairing with a row that shows them
 * no holders either.
 *
 * A miss still renders as "לא ניתן להציג" rather than as an error, because one honest
 * case remains: the mirror can be up to a drain tick ahead of a deletion, so a row can
 * outlive its document by ~60s.
 *
 * The two boxes describe the DEFAULT mode, which the edit dialog still uses. The CRM
 * panel passes `options.preferOwn`, which re-routes a colleague's row through the OWN
 * branch and never calls `get-details` at all — see the option's doc.
 */

export type CrmPanelContactState = {
  contact: RealEstateContact | null;
  loading: boolean;
  /** The read failed (network / 5xx). Distinct from "resolved, and there is nothing there". */
  error: Error | null;
  /** Resolved, but no accessible record came back. */
  missing: boolean;
  /** True when the viewer owns the doc — i.e. the live-listener branch was used. */
  isOwn: boolean;
  /**
   * The ROW belongs to the viewer (or no owner was given). Distinct from `isOwn` under
   * `preferOwn`, where the viewer's own COPY can hydrate a colleague's row — the panel
   * needs the difference to know which captions to show and, crucially, that a patch of
   * this document must NOT be pushed onto the colleague's list row.
   */
  rowIsOwn: boolean;
};

const sameEmail = (a: string | null | undefined, b: string | null | undefined): boolean =>
  Boolean(a) && Boolean(b) && a!.trim().toLowerCase() === b!.trim().toLowerCase();

export default function useCrmPanelContact(
  contactId: string | null,
  /**
   * The row's `ownerUserId`. Absent is read as "the viewer's own" — the workspace is the
   * `mine` view today, and the own branch cannot leak in any case (it reads the viewer's
   * own subcollection). An owner that differs from the viewer is what routes to the server.
   */
  ownerUserId: string | null | undefined,
  options?: {
    /**
     * OWN-FIRST over a colleague's row — the CRM panel's mode (2026-08-26 feedback:
     * "clicking a contact in the org tab that is not ours should open the contact
     * timeline… show the card only if saved for us too").
     *
     * Contact ids are the international phone, so the viewer's copy of the same person
     * SHARES the row's id. With this flag the viewer's own book is what hydrates: their
     * copy exists → it is served with `isOwn: true` (full card, editable — it is their
     * document); it doesn't → the state resolves `missing` WITHOUT ever calling
     * `get-details` — the colleague's document is deliberately not fetched at all, and
     * the panel renders its timeline-only body instead.
     *
     * OPT-IN, and the edit dialog must never pass it: an org admin who clicks עריכה on
     * a teammate's row is editing THAT record — silently swapping in the admin's own
     * copy of the same person would write the wrong document.
     */
    preferOwn?: boolean;
    /**
     * Which scope the own-branch read asks under — the POOLED book's one extra question.
     * A pooled member's office tab shows records they do not hold; under `mine` the
     * server narrows the office book to the viewer's own list and answers "not found"
     * for those, so the panel passes `"org"` for an unheld office record (decided by
     * `crmPooledRowAccess` in the workspace, from the row's `holderIds`). Personal books
     * never set it. Ignored by the 'firestore' listener.
     */
    readScope?: ContactReadScope;
    orgId?: string | null;
  }
): CrmPanelContactState {
  const { user } = useAuth();
  const viewerEmail = (user?.email as string | undefined) ?? null;
  // "Own" is a fact about the BOOK, not the email: a pooled member's rows carry the
  // office's `org:` key as their owner, and that key is theirs (`useContactBook`). The
  // email until the server has answered — the right key for every personal book.
  const { ownBookKey } = useContactBook();
  const viewerBook = ownBookKey ?? viewerEmail;

  const rowIsOwn = Boolean(viewerBook) && (!ownerUserId || sameEmail(ownerUserId, viewerBook));
  /** Whether the OWN-BOOK listener drives this hydration. See `options.preferOwn`. */
  const ownBranch = rowIsOwn || Boolean(options?.preferOwn);

  // Own branch. Passing null keeps the hook a no-op for somebody else's contact, so no
  // listener is ever opened on a document the viewer is not entitled to — and under
  // `preferOwn` the listener still only ever reads the VIEWER's own subcollection.
  const live = useLiveContact(
    ownBranch ? contactId : null,
    options?.readScope === "org" ? { scope: "org", orgId: options.orgId ?? null } : undefined
  );

  // Org branch.
  const [remote, setRemote] = useState<{ id: string; contact: RealEstateContact | null } | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState<Error | null>(null);

  // `useWebotServer()` hands back a context object rebuilt on every provider render, so its
  // functions are not referentially stable. Held in a ref and kept OUT of the effect's deps —
  // depending on it would refire this fetch on unrelated re-renders of the whole app shell.
  const { getContactDetails } = useWebotServer();
  const getContactDetailsRef = useRef(getContactDetails);
  getContactDetailsRef.current = getContactDetails;

  // Stale-response guard. Arrowing down a list fires overlapping reads and the network
  // does not promise to resolve them in order; without the sequence stamp the panel can
  // settle on the contact BEFORE the one on screen.
  const seq = useRef(0);

  useEffect(() => {
    if (!contactId || ownBranch) {
      setRemote(null);
      setRemoteLoading(false);
      setRemoteError(null);
      return undefined;
    }

    const mySeq = ++seq.current;
    let cancelled = false;
    setRemoteLoading(true);
    setRemoteError(null);

    void (async () => {
      try {
        const response = await getContactDetailsRef.current(contactId, ["contacts"]);
        if (cancelled || mySeq !== seq.current) return;
        const entries = response?.[0]?.contactObjectsAcrossUsers ?? [];
        // A phone can be held by several agents in the same agency; take the record
        // belonging to the row's owner, never "the first one the server happened to sort
        // first" — that would show a colleague's copy of the same person under this row.
        const match = entries.find((entry) => sameEmail(entry.user?.userId, ownerUserId)) ?? null;
        setRemote({
          id: contactId,
          // Clone before post-processing: `postProcessContact` mutates, and the response
          // object is shared with anything else holding it.
          contact: match?.contact
            ? postProcessContact({ ...match.contact }, match.user?.userId ?? ownerUserId ?? "")
            : null
        });
      } catch (error) {
        if (cancelled || mySeq !== seq.current) return;
        setRemoteError(error as Error);
      } finally {
        if (!cancelled && mySeq === seq.current) setRemoteLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contactId, ownBranch, ownerUserId]);

  if (!contactId) {
    return { contact: null, loading: false, error: null, missing: false, isOwn: rowIsOwn, rowIsOwn };
  }

  if (ownBranch) {
    // Clone before post-processing for the same reason as above — `live.contact` is the
    // hook's own state object and re-used across renders.
    const contact = live.contact
      ? postProcessContact({ ...live.contact }, viewerEmail ?? "")
      : null;
    return {
      contact,
      loading: live.loading,
      error: live.error,
      missing: !live.loading && !live.error && !live.contact,
      // Under `preferOwn` on a colleague's row, "own" is an answer, not an input: the
      // document is the viewer's own exactly when one came back.
      isOwn: rowIsOwn || Boolean(contact),
      rowIsOwn
    };
  }

  // A response for a previous contact must never render under the current one.
  const fresh = remote?.id === contactId ? remote : null;
  return {
    contact: fresh?.contact ?? null,
    loading: remoteLoading || (!fresh && !remoteError),
    error: remoteError,
    missing: Boolean(fresh) && !fresh!.contact,
    isOwn: false,
    rowIsOwn
  };
}
